import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { ReactElement, useCallback, useState } from "react";
import { TaskType } from "../../../types/TaskType";
import styles from "./TaskTypesEditPage.module.css";
import {
  useUpdateTaskType,
  useTaskTypes,
} from "../../../services/useTaskTypes";
import { HttpError } from "@coworker/apprestructured/src/shared/types/exception";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import TaskTypeForm, {
  ErrorMessageAndException,
} from "../../../components/TaskTypeForm/TaskTypeForm";
import { useParams } from "react-router";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import { useTranslation } from "@coworker/locales";

export default function TaskTypesEditPage() {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<ReactElement | null>(null);

  const { id: idString } = useParams<{ id: string }>();
  const id = idString ?? "";
  const { data: taskTypes } = useTaskTypes([id]);
  const { mutateAsync: mutateTaskType } = useUpdateTaskType();
  const { t } = useTranslation();

  const showErrors = (errors: ErrorMessageAndException[]) => {
    const errorTexts: string[] = errors.map(({ message: text, exception }) => {
      const exceptionText =
        exception instanceof Error
          ? exception.toString()
          : exception instanceof HttpError
            ? `${exception.statusText}(${exception.status})`
            : "unknown exception";
      return `${text}, ${exceptionText}`;
    });

    console.warn(errorTexts.join("\n"));
    setError(
      <>
        {errorTexts.map((text) => (
          <p>{text}</p>
        ))}
      </>
    );
  };

  const onSave = useCallback(
    async (errors: ErrorMessageAndException[], taskType: TaskType) => {
      const allErrors = [...errors];

      if (!allErrors.length) {
        try {
          await mutateTaskType(taskType);
        } catch (exception) {
          allErrors.push({
            message: "Failed to save taskType to server",
            exception,
          });
        }
      }

      if (allErrors.length) {
        showErrors(allErrors);
        return false;
      } else {
        console.info("TaskType updated!");
        return true;
      }
    },
    [mutateTaskType]
  );

  if (!taskTypes || !taskTypes.length) {
    return <LinkbarLoading />;
  }

  return (
    <>
      <Linkbar
        items={[
          {
            label: "Task types",
            to: "/task-types",
          },
          {
            label: `${t(taskTypes[0]!.label)}`,
            to: `/task-types/${id}`,
          },
        ]}
        currentLabel="Edit"
        currentAsH1
      />

      <div className={styles["task-types-create-page"]}>
        <TaskTypeForm<TaskType>
          readOnly={false}
          onSave={onSave}
          taskTypeValues={taskTypes[0]! as TaskType}
          buttonText="Update task type"
        />
      </div>

      <FixaToast
        className={styles["task-types-error-toast"]}
        text={error ?? ""}
        isOpen={!!error}
        onCloseRequest={() => setError(null)}
      />

      <FixaToast
        className={styles["task-types-success-toast"]}
        text={"TaskType updated!"}
        isOpen={!!success}
        onCloseRequest={() => {
          setSuccess(false);
        }}
      />
    </>
  );
}
